import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { parseFechaLocal } from '../../../utils/fechas';
import { getEventosAdmin, getPostulaciones, getPostulacionesHistorial, aprobarEvento, rechazarEvento } from '../../../services/eventosService';
import {
  guardarUrlLiftingCastEvento,
  normalizarUrlLiftingCast,
  sincronizarLiftingCastEvento,
} from '../../../services/liftingcastService';
import ConfirmModal from '../components/ConfirmModal';
import AdminSpinner from '../components/AdminSpinner';
import { useToast } from '../context/ToastContext';

/* =========================
   CONFIG VISUAL
========================= */
const estadoColor = {
  publicado: '#2e7d32',
  propuesto: '#ef6c00',
  pendiente: '#f9a825',
  aprobada: '#2e7d32',
  rechazada: '#c62828',
  cancelado: '#546e7a',
  cerrado: '#1565c0',
  en_curso: '#0277bd',
  finalizado: '#455a64'
};

const styles = {
  container: {
    padding: '24px',
    maxWidth: '1600px'
  },
  header: {
    marginBottom: '20px'
  },
  title: {
    margin: 0,
    fontSize: '28px',
    fontWeight: 700
  },
  subtitle: {
    color: '#666',
    marginTop: '6px'
  },
  tabs: {
    display: 'flex',
    gap: '10px',
    marginBottom: '20px'
  },
  tabBtn: (active) => ({
    padding: '10px 16px',
    borderRadius: '8px',
    border: active ? '2px solid #1a4d8c' : '1px solid #ddd',
    background: active ? '#e3f2fd' : '#fff',
    cursor: 'pointer',
    fontWeight: 600
  }),
  card: {
    background: '#fff',
    borderRadius: '12px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
    padding: '12px'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse'
  },
  th: {
    textAlign: 'left',
    padding: '12px',
    fontSize: '13px',
    color: '#555',
    borderBottom: '1px solid #e0e0e0'
  },
  td: {
    padding: '12px',
    borderBottom: '1px solid #f0f0f0',
    fontSize: '14px'
  },
  badge: (color) => ({
    background: color,
    color: '#fff',
    padding: '5px 14px',
    borderRadius: '16px',
    fontSize: '12px',
    fontWeight: 600,
    textTransform: 'capitalize'
  }),
  actionBtn: {
    padding: '6px 12px',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 600,
    background: '#1a4d8c',
    color: '#fff',
    marginRight: '6px'
  },
  approveBtn: {
    padding: '6px 12px',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 600,
    background: '#2e7d32',
    color: '#fff',
    marginRight: '6px'
  },
  rejectBtn: {
    padding: '6px 12px',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 600,
    background: '#c62828',
    color: '#fff'
  },
  empty: {
    textAlign: 'center',
    padding: '24px',
    color: '#666'
  },  
  btnGhost: { padding: '8px 14px', background: '#f4f6f8', color: '#111', border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 },
  liftingcastCell: {
    minWidth: '260px',
    maxWidth: '320px'
  },
  liftingcastInput: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '7px 10px',
    border: '1px solid #ddd',
    borderRadius: '8px',
    fontSize: '13px'
  },
  liftingcastActions: {
    display: 'flex',
    gap: '6px',
    marginTop: '8px',
    flexWrap: 'wrap'
  },
  liftingcastMeta: {
    fontSize: '12px',
    color: '#666',
    marginTop: '6px'
  },
  btnSync: {
    padding: '6px 12px',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 600,
    background: '#0f766e',
    color: '#fff'
  },
};

/* =========================
   COMPONENTE
========================= */
const EventosView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const tabFromUrl = query.get('tab');

  const [tab, setTab] = useState(
    tabFromUrl === 'postulaciones' ? 'postulaciones' : tabFromUrl === 'historial' ? 'historial' : 'eventos'
  );
  const [eventos, setEventos] = useState([]);
  const [postulaciones, setPostulaciones] = useState([]);
  const [historialPostulaciones, setHistorialPostulaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmAprobarId, setConfirmAprobarId] = useState(null);
  const [confirmRechazarId, setConfirmRechazarId] = useState(null);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [urlsLiftingCast, setUrlsLiftingCast] = useState({});
  const [syncingId, setSyncingId] = useState(null);
  const { showToast } = useToast();

  /* =========================
     FETCHS
  ========================= */
  const fetchEventos = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const data = await getEventosAdmin();
      const lista = data || [];
      setEventos(lista);
      setUrlsLiftingCast(
        Object.fromEntries(lista.map((e) => [e.id, e.liftingcast_url || '']))
      );
    } catch (e) {
      setError(e.message);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const fetchPostulaciones = async () => {
    try {
      setLoading(true);
      const [data, eventosList] = await Promise.all([
        getPostulaciones(),
        getEventosAdmin().catch(() => [])
      ]);
      const list = data || [];
      setPostulaciones(list);
      if (list.length > 0) {
        setEventos(eventosList || []);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistorialPostulaciones = async () => {
    try {
      setLoading(true);
      const data = await getPostulacionesHistorial();
      setHistorialPostulaciones(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmAprobar = async () => {
    if (!confirmAprobarId) return;
    setApproving(true);
    try {
      await aprobarEvento(confirmAprobarId);
      setConfirmAprobarId(null);
      fetchPostulaciones();
    } finally {
      setApproving(false);
    }
  };

  const handleConfirmRechazar = async () => {
    if (!confirmRechazarId) return;
    setRejecting(true);
    try {
      await rechazarEvento(confirmRechazarId);
      setConfirmRechazarId(null);
      fetchPostulaciones();
    } finally {
      setRejecting(false);
    }
  };

  const handleSincronizarLiftingCast = async (evento) => {
    const cruda = urlsLiftingCast[evento.id] || evento.liftingcast_url || '';
    const canonica = normalizarUrlLiftingCast(cruda);
    if (!canonica) {
      showToast('Pega la URL del meet en LiftingCast (se guardará como /results)', 'error');
      return;
    }

    setUrlsLiftingCast((prev) => ({ ...prev, [evento.id]: canonica }));
    setSyncingId(evento.id);
    try {
      const { mensaje } = await sincronizarLiftingCastEvento(evento.id, canonica);
      showToast(mensaje, 'success');
      await fetchEventos(true);
    } catch (err) {
      showToast(err.message || 'No se pudo sincronizar LiftingCast', 'error');
    } finally {
      setSyncingId(null);
    }
  };

  const handleGuardarUrlLiftingCast = async (evento, valor) => {
    const cruda = (valor ?? urlsLiftingCast[evento.id] ?? '').trim();
    const canonica = cruda ? normalizarUrlLiftingCast(cruda) : '';
    const guardada = evento.liftingcast_url || '';
    if ((canonica || '') === guardada) return;

    if (cruda && !canonica) {
      showToast('URL de LiftingCast no válida', 'error');
      return;
    }

    try {
      await guardarUrlLiftingCastEvento(evento.id, canonica);
      setUrlsLiftingCast((prev) => ({ ...prev, [evento.id]: canonica }));
    } catch (err) {
      showToast(err.message || 'No se pudo guardar el link de LiftingCast', 'error');
    }
  };

  useEffect(() => {
    if (tabFromUrl === 'postulaciones' && tab !== 'postulaciones') setTab('postulaciones');
    if (tabFromUrl === 'historial' && tab !== 'historial') setTab('historial');
  }, [tabFromUrl]);

  const setTabAndUrl = (newTab) => {
    setTab(newTab);
    const params = new URLSearchParams(location.search);
    if (newTab === 'postulaciones') params.set('tab', 'postulaciones');
    else if (newTab === 'historial') params.set('tab', 'historial');
    else params.delete('tab');
    navigate(`/admin?${params.toString()}`, { replace: true });
  };

  useEffect(() => {
    if (tab === 'eventos') fetchEventos();
    else if (tab === 'postulaciones') fetchPostulaciones();
    else if (tab === 'historial') fetchHistorialPostulaciones();
  }, [tab]);

  /* =========================
     RENDER
  ========================= */
  if (loading) return <AdminSpinner label="Cargando eventos…" />;
  if (error) return <p style={{ color: '#c62828', padding: 16 }}>{error}</p>;

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>Eventos</h1>
        <p style={styles.subtitle}>
          Gestión administrativa de eventos deportivos
        </p>
      </div>

      {/* Tabs + Agregar evento */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '20px' }}>
        <div style={styles.tabs}>
          <button
            style={styles.tabBtn(tab === 'eventos')}
            onClick={() => setTabAndUrl('eventos')}
          >
            Eventos publicados
          </button>

          <button
            style={styles.tabBtn(tab === 'postulaciones')}
            onClick={() => setTabAndUrl('postulaciones')}
          >
            Postulaciones
          </button>

          <button
            style={styles.tabBtn(tab === 'historial')}
            onClick={() => setTabAndUrl('historial')}
          >
            Historial de Postulaciones
          </button>
        </div>
        {tab === 'eventos' && (
          <button
            type="button"
            style={{ ...styles.actionBtn, marginRight: 0 }}
            onClick={() => navigate('/admin?view=eventos&edit=new')}
          >
            Agregar evento
          </button>
        )}
      </div>

      {/* Contenido */}
      <div style={styles.card}>
        {/* EVENTOS */}
        {tab === 'eventos' && (
          eventos.length === 0 ? (
            <p style={styles.empty}>No hay eventos publicados</p>
          ) : (
            <div className="admin-table-scroll">
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Nombre</th>
                  <th style={styles.th}>Tipo</th>
                  <th style={styles.th}>Fecha inicio</th>
                  <th style={styles.th}>Ciudad</th>
                  <th style={styles.th} title="Cupos disponibles / cupos totales">Cupos (disp./total)</th>
                  <th style={styles.th}>Estado</th>
                  <th style={{ ...styles.th, textAlign: 'center' }}>
                    Acciones
                  </th>
                  <th style={styles.th}>LiftingCast</th>
                </tr>
              </thead>
              <tbody>
                {eventos.map(e => (
                  <tr key={e.id}>
                    <td style={styles.td}>{e.nombre}</td>
                    <td style={styles.td}>{e.tipo_evento}</td>
                    <td style={styles.td}>
                      {parseFechaLocal(e.fecha_inicio)?.toLocaleDateString('es-CL') || '—'}
                    </td>
                    <td style={styles.td}>{e.ciudad || '—'}</td>
                    <td style={styles.td}>
                      {e.cupos_maximos != null
                        ? `${e.cupos_disponibles ?? e.cupos_maximos}/${e.cupos_maximos}`
                        : (e.cupos_disponibles ?? '—')}
                    </td>
                    <td style={styles.td}>
                      <span style={styles.badge(estadoColor[e.estado])}>
                        {e.estado}
                      </span>
                    </td>
                    <td style={{ ...styles.td, textAlign: 'center' }}>
                      <button
                        style={styles.actionBtn}
                        onClick={() => navigate(`/admin?view=eventos&participantes=${e.id}`)}
                        title="Ver participantes y afiliación"
                      >
                        Participantes
                      </button>
                      <button
                        style={styles.btnGhost}
                        onClick={() => navigate(`/admin?view=eventos&edit=${e.id}`)}
                      >
                        Editar
                      </button>
                    </td>
                    <td style={{ ...styles.td, ...styles.liftingcastCell }}>
                      <input
                        type="url"
                        style={styles.liftingcastInput}
                        placeholder="https://liftingcast.com/meets/…/results"
                        value={urlsLiftingCast[e.id] ?? e.liftingcast_url ?? ''}
                        onChange={(ev) => setUrlsLiftingCast((prev) => ({
                          ...prev,
                          [e.id]: ev.target.value
                        }))}
                        onBlur={(ev) => {
                          const cruda = ev.target.value || '';
                          const canonica = normalizarUrlLiftingCast(cruda) || '';
                          if (canonica && canonica !== cruda) {
                            setUrlsLiftingCast((prev) => ({ ...prev, [e.id]: canonica }));
                          }
                          handleGuardarUrlLiftingCast(e, canonica || cruda);
                        }}
                      />
                      <div style={styles.liftingcastActions}>
                        <button
                          type="button"
                          style={styles.btnSync}
                          disabled={syncingId === e.id}
                          onClick={() => handleSincronizarLiftingCast(e)}
                        >
                          {syncingId === e.id ? 'Sincronizando…' : 'Sincronizar'}
                        </button>
                        {e.liftingcast_meet_id && (
                          <button
                            type="button"
                            style={styles.btnGhost}
                            onClick={() => navigate(`/admin?view=eventos&liftingcast=${e.id}`)}
                          >
                            Ver nómina
                          </button>
                        )}
                      </div>
                      {e.liftingcast_meet_id ? (
                        <div style={styles.liftingcastMeta}>
                          {e.liftingcast_total_atletas || 0} atletas
                          {e.liftingcast_sincronizado_at
                            ? ` · ${new Date(e.liftingcast_sincronizado_at.endsWith('Z') ? e.liftingcast_sincronizado_at : `${e.liftingcast_sincronizado_at}Z`).toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short' })}`
                            : ''}
                        </div>
                      ) : (
                        <div style={styles.liftingcastMeta}>
                          Se guarda siempre como /results
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>
          )
        )}

        {/* POSTULACIONES */}
        {tab === 'postulaciones' && (
          postulaciones.length === 0 ? (
            <p style={styles.empty}>No hay postulaciones pendientes</p>
          ) : (
            <div className="admin-table-scroll">
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Evento</th>
                  <th style={styles.th}>Tipo</th>
                  <th style={styles.th}>Fechas</th>
                  <th style={styles.th}>Ciudad / Región</th>
                  <th style={styles.th}>Postulado por</th>
                  <th style={styles.th}>Fecha postulación</th>
                  <th style={{ ...styles.th, textAlign: 'center' }}>
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody>
                {postulaciones.map(p => {
                  const ev = p.evento || eventos.find(e => e.id === p.evento_id);
                  return (
                  <tr key={p.id}>
                    <td style={styles.td}>
                      <strong>{p.nombre_evento}</strong>
                      {(ev?.descripcion || p.evento?.descripcion) && (
                        <div style={{ fontSize: '12px', color: '#666', marginTop: '4px', maxWidth: '220px' }} title={ev?.descripcion || p.evento?.descripcion}>
                          {(ev?.descripcion || p.evento?.descripcion || '').length > 80
                            ? `${(ev?.descripcion || p.evento?.descripcion).slice(0, 80)}…`
                            : (ev?.descripcion || p.evento?.descripcion)}
                        </div>
                      )}
                    </td>
                    <td style={styles.td}>{ev?.tipo_evento || p.evento?.tipo_evento || '—'}</td>
                    <td style={styles.td}>
                      {(ev?.fecha_inicio || p.evento?.fecha_inicio) && (
                        <span>
                          {new Date(ev?.fecha_inicio || p.evento.fecha_inicio).toLocaleDateString()}
                          {(ev?.fecha_fin || p.evento?.fecha_fin) && ` – ${new Date(ev?.fecha_fin || p.evento.fecha_fin).toLocaleDateString()}`}
                        </span>
                      ) || '—'}
                    </td>
                    <td style={styles.td}>
                      {[(ev?.ciudad || p.evento?.ciudad), (ev?.region || p.evento?.region)].filter(Boolean).join(', ') || '—'}
                    </td>
                    <td style={styles.td}>
                      <span>{p.nombre_postulador || p.postulado_por}</span>
                      {p.nombre_postulador && (
                        <div style={{ fontSize: '12px', color: '#666' }}>{p.postulado_por}</div>
                      )}
                    </td>
                    <td style={styles.td}>
                      {new Date(p.fecha).toLocaleDateString()}
                    </td>
                    <td style={{ ...styles.td, textAlign: 'center' }}>
                      <button
                        style={styles.approveBtn}
                        onClick={() => setConfirmAprobarId(p.evento_id)}
                      >
                        Aprobar
                      </button>
                      <button
                        style={styles.rejectBtn}
                        onClick={() => setConfirmRechazarId(p.evento_id)}
                      >
                        Rechazar
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
              </table>
            </div>
          )
        )}

        {/* HISTORIAL DE POSTULACIONES */}
        {tab === 'historial' && (
          historialPostulaciones.length === 0 ? (
            <p style={styles.empty}>No hay historial de postulaciones (aprobadas o rechazadas)</p>
          ) : (
            <div className="admin-table-scroll">
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Evento</th>
                  <th style={styles.th}>Postulado por</th>
                  <th style={styles.th}>Fecha postulación</th>
                  <th style={styles.th}>Estado</th>
                  <th style={styles.th}>Revisado por</th>
                  <th style={styles.th}>Fecha revisión</th>
                </tr>
              </thead>
              <tbody>
                {historialPostulaciones.map(p => (
                  <tr key={p.id}>
                    <td style={styles.td}>
                      <strong>{p.nombre_evento || (p.evento && p.evento.nombre) || '—'}</strong>
                    </td>
                    <td style={styles.td}>
                      <span>{p.nombre_postulador || p.postulado_por}</span>
                      {p.nombre_postulador && (
                        <div style={{ fontSize: '12px', color: '#666' }}>{p.postulado_por}</div>
                      )}
                    </td>
                    <td style={styles.td}>
                      {p.fecha ? new Date(p.fecha).toLocaleDateString() : '—'}
                    </td>
                    <td style={styles.td}>
                      <span style={styles.badge(estadoColor[p.estado] || '#666')}>
                        {p.estado}
                      </span>
                    </td>
                    <td style={styles.td}>
                      {p.nombre_revisor ? (
                        <>
                          <span>{p.nombre_revisor}</span>
                          {p.revisado_por && (
                            <div style={{ fontSize: '12px', color: '#666' }}>{p.revisado_por}</div>
                          )}
                        </>
                      ) : p.revisado_por ? (
                        <span style={{ color: '#666' }}>{p.revisado_por}</span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td style={styles.td}>
                      {p.revisado_at ? new Date(p.revisado_at).toLocaleDateString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>
          )
        )}
      </div>

      <ConfirmModal
        open={!!confirmAprobarId}
        onClose={() => setConfirmAprobarId(null)}
        onConfirm={handleConfirmAprobar}
        title="Aprobar evento"
        message="¿Aprobar este evento? Se publicará en la lista de eventos."
        confirmLabel="Aprobar"
        cancelLabel="Cancelar"
        variant="primary"
        loading={approving}
      />

      <ConfirmModal
        open={!!confirmRechazarId}
        onClose={() => setConfirmRechazarId(null)}
        onConfirm={handleConfirmRechazar}
        title="Rechazar postulación"
        message="¿Rechazar esta postulación? El evento no se publicará."
        confirmLabel="Rechazar"
        cancelLabel="Cancelar"
        variant="danger"
        loading={rejecting}
      />
    </div>
  );
};

export default EventosView;
